import {
  handleSubmitMultilingualForm,
  useCreateNewsPage,
  useGetAllNews,
  useGetNewspageFormData,
  useUpdateNewsPage,
} from "@/api/cms.ts";
import NewsForm from "@/components/cms/NewsForm.tsx";
import { NewsPageForm } from "@/components/cms/NewsPageForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import StyledDrawer from "@/components/StyledDrawer.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Form } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { cmsPageSchema, newsPageContentSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = multilingualFieldSchema(
  cmsPageSchema
    .pick({ pageName: true, published: true })
    .merge(
      newsPageContentSchema.omit({
        id: true,
        news: true,
      }),
    )
    .optional(),
);

export type FormData = z.infer<typeof formSchema>;

const NewsCMS = () => {
  const { t, i18n } = useTranslation();

  const { toast } = useToast();

  const { data: newsPageData } = useGetNewspageFormData();
  const { data: news, refetch } = useGetAllNews();

  const create = useCreateNewsPage();
  const update = useUpdateNewsPage();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const page = newsPageData?.[lang];
        return {
          ...page?.content,
          published: page?.published ?? false,
          pageName: page?.pageName ?? "",
        };
      }),
    [newsPageData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [form, defaultValues]);

  const onSubmit = (submittedData: FormData) => {
    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = newsPageData?.[key as SupportedLocale];

      if (!localeData) return;

      const { pageName, published, ...content } = localeData;

      const mutation = baseData?.content.id ? update : create;
      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("newsSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("newsErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });

    // if (CMSID) {
    //   createOrUpdateNewsPage.mutate(
    //     {
    //       cmsId: CMSID,
    //       newsPageId: newsPage?.id,
    //       pageName: nameOnglet,
    //       news: news,
    //       publishedPage: isPublished,
    //       contentFR,
    //       contentEN,
    //       pageNameEN: nameOngletEN,
    //     },
    //     {
    //       onSuccess: () => {
    //         toast({
    //           title: t("success"),
    //           description: t("newsSuccessMessageNewsPage"),
    //           duration: 1000,
    //           variant: "success",
    //         });
    //       },
    //       onError: (error) => {
    //         console.log("Error", error);
    //         toast({
    //           title: t("error"),
    //           description: t("newsErrorMessageNewsPage"),
    //           variant: "error",
    //           duration: 1000,
    //         });
    //       },
    //     },
    //   );
    // }
  };

  return (
    <CMSLayout pageName={t("cmsTabNews")} formIds={["news-page"]}>
      <Form {...form}>
        <form
          id="news-page"
          onSubmit={handleSubmitMultilingualForm(form, newsPageData, onSubmit)}
          className="flex flex-col gap-8 w-full"
        >
          <div className="flex gap-4">
            <DoubleMultilingualField
              corner={(lang) => (
                <Switch dispLabel={false} name={`${lang}.published`} />
              )}
              render={(lang) => <NewsPageForm lang={lang} />}
            />
          </div>
          <div className=" w-full flex justify-between">
            <p className="text-[#0c111d] text-base font-medium font-['Raleway'] leading-normal">
              {news?.length ?? 0} {t("publicationsLabel")}
            </p>
            <StyledDrawer
              className="overflow-y-auto"
              title={t("cmsAddNewsTitle")}
              trigger={
                <Button variant="outline">{t("cmsAddNewsButton")}</Button>
              }
            >
              <NewsForm refetch={refetch} />
            </StyledDrawer>
          </div>
          {news && (
            <div className="w-full grid grid-cols-3 gap-8">
              {news.map((newsItem) => {
                return (
                  <StyledDrawer
                    className="overflow-y-auto"
                    key={newsItem.id}
                    title={t("cmsAddNewsTitle")}
                    trigger={
                      <div className="h-[432px] flex-col justify-start items-start gap-5 inline-flex cursor-pointer">
                        <img
                          className="h-60 relative"
                          src={newsItem.attachments?.[0]?.mediumPublicUrl}
                          alt={
                            newsItem.title?.[
                              i18n.language as SupportedLocale
                            ] ?? t("newsImage")
                          }
                        />
                        <div className="self-stretch h-[172px] flex-col justify-start items-start gap-6 flex">
                          <div className="self-stretch h-[172px] flex-col justify-start items-start gap-2 flex">
                            <p className="text-start self-stretch text-[#090909] text-sm font-semibold font-['Instrument Sans'] leading-tight">
                              {newsItem.date
                                ? new Date(newsItem.date).toLocaleDateString(
                                    "fr-FR",
                                    {
                                      year: "numeric",
                                      day: "2-digit",
                                      month: "short",
                                    },
                                  )
                                : t("unknownDate")}
                            </p>
                            <p className="text-start text-[#090909] text-2xl font-semibold font-['Instrument Sans'] ">
                              {
                                newsItem.title?.[
                                  i18n.language as SupportedLocale
                                ]
                              }
                            </p>
                            <div
                              className="text-start self-stretch text-[#475466] text-base font-normal font-['Instrument Sans'] leading-normal"
                              dangerouslySetInnerHTML={{
                                __html:
                                  newsItem.content?.[
                                    i18n.language as SupportedLocale
                                  ] ?? "",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <NewsForm newsItem={newsItem} refetch={refetch} />
                  </StyledDrawer>
                );
              })}
            </div>
          )}
        </form>
      </Form>
    </CMSLayout>
  );
};

export default NewsCMS;
